import React from 'react';
import {Dialog, DialogContent, DialogTitle, Tooltip} from '@mui/material';
import AttachmentIcon from '@mui/icons-material/AttachFile';
import {useTranslation} from 'react-i18next';
import {useTheme} from '@mui/material/styles';
import {ButtonsContainer, StyledIconButton} from './FilePicker.styled';
import FileFromDiscButton from './components/FileFromDiscButton';
import {CancelButton} from '../../common/ActionButtons';
import FileFromSourceButton from './components/FileFromSourceButton';
import {useSelectedFiles} from '../../../contexts/SelectedFilesContext';
import {useSnackbar} from '../../../contexts/SnackbarContext';
import {CHAT_CONSTANTS} from '../../../config/chat.constants';
import FileSelectionDialog from "../../file-selection/FileSelectionDialog";

interface FilePickerProps {
    isGenerating: boolean;
    isAnyFileInConversation: boolean;
    disabled?: boolean;
}

const FilePicker = ({isGenerating, isAnyFileInConversation, disabled = false}: FilePickerProps) => {
    const {t} = useTranslation();
    const {showSnackbar} = useSnackbar();
    const theme = useTheme();

    const {attachedFiles, setAttachedFiles} = useSelectedFiles();

    const [open, setOpen] = React.useState(false);
    const [showFileSelection, setShowFileSelection] = React.useState(false);

    const handleOpen = () => setOpen(true);

    const handleClose = () => {
        setOpen(false);
        setShowFileSelection(false);
    };

    const handleOpenFileSelectionDialog = () => {
        setOpen(false);
        setShowFileSelection(true);
    };

    const handleCloseFileSelectionDialog = (isCancelled: boolean) => {
        setShowFileSelection(false);
        setOpen(isCancelled);
    }

    const handleFileChangeAndClose = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files || event.target.files.length === 0) return;

        const selectedFiles = Array.from(event.target.files);
        if (attachedFiles.length + selectedFiles.length > CHAT_CONSTANTS.MAX_DISK_ATTACHMENTS) {
            showSnackbar(t('chatInput.fileLimitReachedWarning', {maxFiles: CHAT_CONSTANTS.MAX_DISK_ATTACHMENTS}), 'warning');
            event.target.value = '';
            return;
        }

        setAttachedFiles([...attachedFiles, ...selectedFiles]);
        handleClose();
    };

    const disableFilePicker = isGenerating || isAnyFileInConversation || disabled;

    return (
        <>
            <Tooltip title={t('attachFiles.addFile')}>
                <span>
                    <StyledIconButton
                        aria-label={t('attachFiles.addFile') || ''}
                        onClick={handleOpen}
                        disabled={disableFilePicker}
                        $disabled={disableFilePicker}
                    >
                        <AttachmentIcon/>
                    </StyledIconButton>
                </span>
            </Tooltip>

            {open && (
                <Dialog open={true} onClose={handleClose} fullWidth maxWidth="xs">
                    <DialogTitle>{t('attachFiles.dialogTitle')}</DialogTitle>

                    <DialogContent>
                        <ButtonsContainer>
                            <FileFromDiscButton handleFileChange={handleFileChangeAndClose}
                                                disable={disableFilePicker}/>
                            <FileFromSourceButton onClick={handleOpenFileSelectionDialog}
                                                  disable={disableFilePicker}/>
                        </ButtonsContainer>

                        <div style={{display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px'}}>
                            <CancelButton onClick={handleClose}>
                                {t('common.cancel')}
                            </CancelButton>
                        </div>
                    </DialogContent>
                </Dialog>
            )}

            {showFileSelection && (
                <FileSelectionDialog open={true} onClose={handleCloseFileSelectionDialog} />
            )}
        </>
    );
};

export default FilePicker;
